import {ChangeDetectionStrategy, Component, DestroyRef, inject, signal} from '@angular/core';
import {
    Event,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationSkipped,
    NavigationStart,
    Router,
    RouterOutlet,
} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {VulcanNotifyComponent} from '@eagle-i/vulcan-component-library';
import {LoadingIndicatorComponent} from 'src/app/shared/components/loading-indicator/loading-indicator.component';

/* v8 ignore next */
declare let gtag: (type: 'config', key: string, options: {page_path: string}) => void;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, LoadingIndicatorComponent, VulcanNotifyComponent],
})
export class AppComponent {
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private activeNavigationId?: number;
    private navigationDelayHandle?: number;

    /** Template variable: Shows the app-wide mask only after a navigation takes long enough to need feedback. */
    public readonly isRouteLoading = signal(false);

    constructor() {
        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.handleRouterEvent(event));

        this.destroyRef.onDestroy(() => {
            this.clearNavigationDelay();
            this.isRouteLoading.set(false);
        });
    }

    private handleRouterEvent(event: Event): void {
        if (event instanceof NavigationStart) {
            this.startNavigation(event.id);
            return;
        }

        if (event instanceof NavigationEnd) {
            this.trackPageView(event);
            this.finishNavigation(event.id);
            return;
        }

        if (
            event instanceof NavigationCancel ||
            event instanceof NavigationError ||
            event instanceof NavigationSkipped
        ) {
            this.finishNavigation(event.id);
        }
    }

    private startNavigation(navigationId: number): void {
        this.activeNavigationId = navigationId;
        this.clearNavigationDelay();
        this.navigationDelayHandle = window.setTimeout(() => {
            if (this.activeNavigationId === navigationId) {
                this.isRouteLoading.set(true);
            }
        }, 150);
    }

    private finishNavigation(navigationId: number): void {
        if (this.activeNavigationId !== navigationId) {
            return;
        }

        this.activeNavigationId = undefined;
        this.clearNavigationDelay();
        this.isRouteLoading.set(false);
    }

    private clearNavigationDelay(): void {
        if (this.navigationDelayHandle !== undefined) {
            window.clearTimeout(this.navigationDelayHandle);
            this.navigationDelayHandle = undefined;
        }
    }

    private trackPageView(event: NavigationEnd): void {
        const analyticsKey = (window as Window & {ga_ei_key?: string}).ga_ei_key;
        if (analyticsKey) {
            gtag('config', analyticsKey, {page_path: event.urlAfterRedirects});
        }
    }
}
